import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mic, Square, Loader2, CheckCircle2, AlertCircle, RefreshCw, Volume2, Play, Pause, Repeat, Infinity as InfIcon } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";
import DOMPurify from "dompurify";
import { SURAHS } from "../data/surahs";
import { audioUrl, TEACHERS } from "../data/reciters";
import "../styles/tajweed.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AQC_TAJWEED = (n) => `https://api.alquran.cloud/v1/surah/${n}/quran-tajweed`;
const AQC_EN = (n) => `https://api.alquran.cloud/v1/surah/${n}/en.sahih`;

const parseTajweed = (text) => (text || "").replace(
  /\[([a-z])(?::\d+)?\[([^\]]+)\]/g,
  (_, cls, txt) => `<span class="tj-${cls}">${txt}</span>`
);

const VERDICT_STYLES = {
  excellent:   { color: "text-forest", bg: "bg-forest/10", label: "Mā shā' Allāh" },
  good:        { color: "text-forest", bg: "bg-forest/10", label: "Good" },
  needs_work:  { color: "text-gold",   bg: "bg-gold/15",   label: "Needs work" },
  unclear:     { color: "text-clay",   bg: "bg-clay/10",   label: "Unclear" },
};

const REPEAT_OPTIONS = [1, 3, 5, 10, Infinity];

const Pronunciation = () => {
  // ── State ──────────────────────────────────────────────────────────
  const [surah, setSurah] = useState(1);
  const [ayah, setAyah] = useState(1);
  const [teacher, setTeacher] = useState(TEACHERS[0].id);           // reference reciter (default Al-Husary)
  const [repeatCount, setRepeatCount] = useState(3);                // rehearsal repeats
  const [scope, setScope] = useState("ayah");                       // "ayah" | "surah"

  const [tajweedAyahs, setTajweedAyahs] = useState([]);             // marked-up Arabic
  const [enAyahs, setEnAyahs] = useState([]);                       // English translation
  const [loadingSurah, setLoadingSurah] = useState(true);

  // Playback state
  const [playing, setPlaying] = useState(false);
  const [remaining, setRemaining] = useState(0);                    // repeats left
  const [currentAyah, setCurrentAyah] = useState(null);             // ayah being played (during surah-scope)
  const audioRef = useRef(null);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState("");
  const [analysing, setAnalysing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const surahMeta = SURAHS[surah - 1];
  const maxAyah = surahMeta.ayahs;
  const currentTajweedText = tajweedAyahs[ayah - 1]?.text || "";
  const currentEnText = enAyahs[ayah - 1]?.text || "";

  // ── Load surah tajweed + translation ───────────────────────────────
  useEffect(() => {
    let cancel = false;
    setLoadingSurah(true); setTajweedAyahs([]); setEnAyahs([]);
    Promise.all([
      fetch(AQC_TAJWEED(surah)).then((r) => r.json()),
      fetch(AQC_EN(surah)).then((r) => r.json()),
    ]).then(([taj, en]) => {
      if (cancel) return;
      setTajweedAyahs(taj?.data?.ayahs || []);
      setEnAyahs(en?.data?.ayahs || []);
      setLoadingSurah(false);
    }).catch(() => { if (!cancel) setLoadingSurah(false); });
    return () => { cancel = true; };
  }, [surah]);

  // Clip audio playback when settings change
  useEffect(() => { stop(); /* eslint-disable-next-line */ }, [surah, ayah, teacher, scope]);

  // ── Playback ───────────────────────────────────────────────────────
  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlaying(false); setRemaining(0); setCurrentAyah(null);
  };

  const startPlayback = () => {
    const startAyah = scope === "surah" ? 1 : ayah;
    setCurrentAyah(startAyah);
    setRemaining(repeatCount);
    setPlaying(true);
  };

  useEffect(() => {
    if (!playing || !audioRef.current || !currentAyah) return;
    audioRef.current.src = audioUrl(teacher, surah, currentAyah);
    audioRef.current.play().catch(() => {});
  }, [playing, currentAyah, teacher, surah]);

  const onAudioEnded = () => {
    if (scope === "ayah") {
      // Repeat this single ayah `repeatCount` times (or infinite)
      if (remaining === Infinity || remaining > 1) {
        setRemaining((r) => (r === Infinity ? Infinity : r - 1));
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      } else { stop(); }
    } else {
      // Surah scope — advance ayah-by-ayah, then repeat the whole surah
      if (currentAyah < tajweedAyahs.length) {
        setCurrentAyah((n) => n + 1);
      } else if (remaining === Infinity || remaining > 1) {
        setRemaining((r) => (r === Infinity ? Infinity : r - 1));
        setCurrentAyah(1);
      } else { stop(); }
    }
  };

  // ── Recording ──────────────────────────────────────────────────────
  const startRec = async () => {
    stop();
    setError(""); setResult(null); setRecordedBlob(null); setRecordedUrl("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch (e) {
      setError("Microphone access denied. Please allow microphone access and try again.");
    }
  };
  const stopRec = () => { if (mediaRef.current) mediaRef.current.stop(); setRecording(false); };

  // Persist the caller's email so their trial counter is tracked server-side.
  const [studentEmail, setStudentEmail] = useState(() => {
    try { return localStorage.getItem("icc_academy_email") || ""; } catch { return ""; }
  });
  useEffect(() => {
    try { studentEmail ? localStorage.setItem("icc_academy_email", studentEmail) : null; } catch {}
  }, [studentEmail]);

  const analyse = async () => {
    if (!recordedBlob) return;
    setAnalysing(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("surah", surah);
      fd.append("ayah", ayah);
      fd.append("audio", recordedBlob, "recording.webm");
      if (studentEmail.trim()) fd.append("email", studentEmail.trim());
      const r = await axios.post(`${API}/pronunciation/check`, fd);
      setResult(r.data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) {
        setError(e.response?.data?.detail || "Free AI trial is used up. Please subscribe to the Academy Pass.");
      } else {
        setError(e.response?.data?.detail || e.message || "Analysis failed");
      }
    } finally { setAnalysing(false); }
  };

  const reset = () => { setResult(null); setRecordedBlob(null); setRecordedUrl(""); setError(""); };
  const verdictStyle = result ? VERDICT_STYLES[result.verdict] : null;

  // ── UI helpers ─────────────────────────────────────────────────────
  const repeatLabel = (n) => (n === Infinity ? "∞" : `${n}×`);
  const scopeLabel = scope === "ayah" ? "this ayah" : "the whole surah";

  return (
    <div data-testid="pronunciation-page" className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
        <span>/</span>
        <span className="text-forest"><Tx>Pronunciation Coach</Tx></span>
      </div>

      <div className="text-center mb-8">
        <Mic className="w-10 h-10 text-gold mx-auto mb-3" />
        <h1 className="font-heading text-4xl text-forest font-bold mb-3"><Tx>AI Pronunciation Coach</Tx></h1>
        <p className="text-sm text-mute leading-relaxed max-w-xl mx-auto">
          <Tx>Rehearse with the colored-tajweed Mushaf as many times as you need, then record your recitation to get instant feedback.</Tx>
        </p>
      </div>

      <audio ref={audioRef} onEnded={onAudioEnded} onError={onAudioEnded} preload="none" />

      {/* Pickers */}
      <div className="clay-surface rounded-3xl p-6 mb-5 grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Surah</Tx></span>
          <select data-testid="pron-surah" value={surah}
            onChange={(e) => { setSurah(Number(e.target.value)); setAyah(1); setResult(null); }}
            className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm">
            {SURAHS.map((s) => (
              <option key={s.n} value={s.n}>{`${s.n}. ${s.name}`}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Ayah</Tx></span>
          <input data-testid="pron-ayah" type="number" min={1} max={maxAyah} value={ayah}
            onChange={(e) => setAyah(Math.min(maxAyah, Math.max(1, Number(e.target.value) || 1)))}
            className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
          <span className="text-[10px] text-mute mt-1 block">1 – {maxAyah}</span>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Reciting teacher</Tx></span>
          <select data-testid="pron-teacher" value={teacher} onChange={(e) => setTeacher(e.target.value)}
            className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm">
            {TEACHERS.map((t) => (
              <option key={t.id} value={t.id}>{`${t.name} — ${t.style}`}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Rehearse block — colored Tajweed Mushaf + repeat controls */}
      <div className="clay-surface rounded-3xl p-6 mb-5">
        <p className="text-[10px] uppercase tracking-widest text-gold mb-3"><Tx>Rehearse — colored Tajweed Mushaf</Tx></p>

        {loadingSurah ? (
          <div className="text-center py-10 text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : scope === "ayah" ? (
          <div
            data-testid="rehearse-ayah"
            className={`tajweed-verse ${playing ? "is-playing" : ""}`}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseTajweed(currentTajweedText)) }}
          />
        ) : (
          <div data-testid="rehearse-surah" className="space-y-4">
            {tajweedAyahs.map((a, i) => (
              <div key={a.numberInSurah} className="flex items-start gap-3">
                <span className="ayah-num mt-2 flex-shrink-0">{a.numberInSurah}</span>
                <div
                  className={`tajweed-verse !text-xl flex-1 ${playing && currentAyah === a.numberInSurah ? "is-playing" : ""}`}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseTajweed(a.text)) }}
                />
              </div>
            ))}
          </div>
        )}

        {currentEnText && scope === "ayah" && (
          <p className="text-xs text-ink/70 italic mt-4 border-l-2 border-gold/40 pl-3">{currentEnText}</p>
        )}

        {/* Controls */}
        <div className="mt-5 border-t border-forest/10 pt-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-1 flex-wrap">
            <button
              data-testid="rehearse-scope-ayah"
              onClick={() => setScope("ayah")}
              className={`text-xs px-3 py-1.5 rounded-full transition ${scope === "ayah" ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"}`}
            >
              <Tx>Just this ayah</Tx>
            </button>
            <button
              data-testid="rehearse-scope-surah"
              onClick={() => setScope("surah")}
              className={`text-xs px-3 py-1.5 rounded-full transition ${scope === "surah" ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"}`}
            >
              <Tx>Whole surah</Tx>
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1">
              <Repeat className="w-3 h-3" /> <Tx>Repeat</Tx>
            </span>
            <div className="flex gap-1">
              {REPEAT_OPTIONS.map((n) => (
                <button
                  key={String(n)}
                  data-testid={`rehearse-repeat-${n === Infinity ? "inf" : n}`}
                  onClick={() => setRepeatCount(n)}
                  className={`text-xs w-9 h-8 rounded-full transition ${repeatCount === n ? "bg-gold text-forest" : "bg-cream text-mute hover:text-forest"}`}
                >{n === Infinity ? <InfIcon className="w-3 h-3 inline" /> : `${n}×`}</button>
              ))}
            </div>
          </div>

          <button
            data-testid="rehearse-play"
            onClick={playing ? stop : startPlayback}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${playing ? "bg-clay text-sand" : "bg-forest text-sand hover:bg-sage"}`}
          >
            {playing ? <><Pause className="w-4 h-4" /> <Tx>Stop</Tx></> : <><Play className="w-4 h-4" /> <Tx>Rehearse</Tx> {repeatLabel(repeatCount)}</>}
          </button>
        </div>

        {playing && (
          <p className="text-[10px] text-gold text-center mt-3">
            <Tx>Playing</Tx> {scopeLabel} · <Tx>Repeats left</Tx>: {remaining === Infinity ? "∞" : remaining}
            {scope === "surah" && currentAyah && ` · Ayah ${currentAyah}/${tajweedAyahs.length}`}
          </p>
        )}
      </div>

      {/* Recording block */}
      <div className="clay-surface rounded-3xl p-6 mb-6">
        <p className="text-[10px] uppercase tracking-widest text-gold mb-3"><Tx>Now record yourself</Tx></p>
        <p className="text-xs text-mute mb-4">
          <Tx>Once you're comfortable, record yourself reciting the ayah aloud. We'll compare it to the Uthmani reference.</Tx>
        </p>
        {/* Email + trial info — first 3 checks are free, then Academy Pass is required */}
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-widest text-mute">
            <Tx>Your email — to save progress & unlock live tutoring</Tx>
          </span>
          <input
            data-testid="pron-email"
            type="email"
            value={studentEmail}
            onChange={(e) => setStudentEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm"
          />
          <span className="text-[10px] text-mute mt-1 block">
            <Tx>The first 3 AI checks are completely FREE (roughly a 15-minute trial). After that, you'll need an Academy Pass — a single £12 payment that unlocks unlimited use for 30 days.</Tx>
          </span>
        </label>
        <div className="text-center py-4">
          {!recording && !recordedBlob && (
            <button data-testid="pron-record" onClick={startRec}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-forest text-sand hover:bg-sage transition font-medium">
              <Mic className="w-4 h-4" /> <Tx>Start recording</Tx>
            </button>
          )}
          {recording && (
            <button data-testid="pron-stop" onClick={stopRec}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay text-sand animate-pulse font-medium">
              <Square className="w-4 h-4 fill-current" /> <Tx>Stop</Tx>
            </button>
          )}
          {recordedBlob && !recording && !result && (
            <div className="space-y-3">
              <audio src={recordedUrl} controls className="mx-auto" />
              <div className="flex gap-2 justify-center flex-wrap">
                <button data-testid="pron-analyse" onClick={analyse} disabled={analysing}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-forest text-sand hover:bg-sage font-medium disabled:opacity-60">
                  {analysing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {analysing ? <Tx>Analysing…</Tx> : <Tx>Check my recitation</Tx>}
                </button>
                <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cream border border-forest/15 text-forest text-sm">
                  <RefreshCw className="w-4 h-4" /> <Tx>Redo</Tx>
                </button>
              </div>
            </div>
          )}
        </div>
        {error && (
          <div className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3 mt-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{typeof error === "string" ? error : JSON.stringify(error)}</span>
          </div>
        )}
      </div>

      {/* Result block */}
      {result && (
        <div data-testid="pron-result" className="clay-surface rounded-3xl p-6 space-y-5">
          <div className={`flex items-center gap-3 p-4 rounded-2xl ${verdictStyle.bg}`}>
            <span className={`text-3xl font-heading font-bold ${verdictStyle.color}`}>{result.score}%</span>
            <div>
              <p className={`text-[10px] uppercase tracking-widest ${verdictStyle.color}`}>{verdictStyle.label}</p>
              <p className="text-sm text-ink"><Tx>{result.tip}</Tx></p>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Reference (Uthmani)</Tx></p>
            <p className="arabic text-2xl text-forest leading-loose text-right" dir="rtl">{result.reference_text}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Your recitation (Whisper)</Tx></p>
            <p className="arabic text-xl text-ink leading-loose text-right" dir="rtl">
              {result.transcript || <em className="text-mute text-sm not-italic">Nothing was transcribed.</em>}
            </p>
          </div>
          {result.missing?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-clay mb-2"><Tx>Words to work on</Tx></p>
              <div className="flex flex-wrap gap-2">
                {result.missing.map((w, i) => (
                  <span key={i} className="arabic text-lg bg-clay/10 text-clay px-3 py-1 rounded-full">{w}</span>
                ))}
              </div>
            </div>
          )}
          <p className="text-[11px] text-mute italic border-t border-forest/10 pt-3"><Tx>{result.disclaimer}</Tx></p>

          {/* Trial-attempts counter + Academy Pass upsell */}
          {result.has_academy_pass === false && result.trial_attempts_remaining != null && (
            <div data-testid="trial-remaining" className={`rounded-2xl p-3 text-sm ${result.trial_attempts_remaining === 0 ? "bg-clay/10 text-clay" : "bg-gold/10 text-gold"}`}>
              {result.trial_attempts_remaining === 0
                ? <><Tx>You've used all 3 free AI checks.</Tx> <Link to="/academy/pass" className="underline font-semibold"><Tx>Pay £12 for 30 days of unlimited access →</Tx></Link></>
                : <><Tx>Free AI trial:</Tx> <strong>{result.trial_attempts_remaining}</strong> <Tx>free check(s) left.</Tx> <Link to="/academy/pass" className="underline"><Tx>Or pay £12 now to unlock 30 days of unlimited use.</Tx></Link></>
              }
            </div>
          )}

          <button onClick={reset} className="w-full py-3 rounded-full bg-forest text-sand hover:bg-sage font-medium">
            <Tx>Try another ayah</Tx>
          </button>
          {/* CTA — send student from AI coach → live tutor subscription */}
          <Link
            to="/academy/tutors"
            data-testid="cta-book-tutor"
            className="block mt-3 rounded-3xl p-5 bg-gold/15 border-2 border-gold/40 hover:bg-gold/25 transition text-center"
          >
            <p className="text-[10px] uppercase tracking-widest text-gold mb-1"><Tx>Ready for the next step?</Tx></p>
            <p className="font-heading text-lg text-forest font-bold mb-1">
              <Tx>Book a live human tutor</Tx>
            </p>
            <p className="text-xs text-mute">
              <Tx>£8 for one 30-minute lesson · £25 for four lessons a month · £40 for eight lessons a month. Real-time correction with ijāza-holding qurrā'.</Tx>
            </p>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Pronunciation;
